import { ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import styles from "./AssessmentDetailsBlock.module.css";
import {
	CoreBanner,
	CoreButton,
	CoreDateTimePicker,
	CoreErrorIndicator,
	CoreExpandIcon,
	CoreFileTypeSelector,
	CoreMenuItem,
	CoreModal,
	CoreModalDescription,
	CoreSelectSearchProps,
	CoreSelectTextField,
	CoreSkeletonWrapper,
	CoreSwitch,
	CoreTextField,
	coreToast,
	FileTypeOption,
} from "@verity/components/core";
import { Button, FormControlLabel, ListItemText, Stack, Typography } from "@mui/material";
import {
	baseTransKey,
	getAssignmentPointsErrorMessage,
	getModule,
	isAssessmentTitleValid,
	isSelectedCourseValid,
	isSelectedModuleValid,
	UNLIMITED_ATTEMPTS,
	usesModules,
} from "./verityAssessmentHelper";
import i18n from "../../../../i18n";
import { Lms, Module } from "@verity/api-client/types";
import { PortalControllerContext } from "../../../context";
import ArrowRight from "../../../../images/reports/arrowRight.svg?react";
import { VerityAssessmentType, ScoringStrategy } from "@verity/database/prisma";
import apiClient from "../../../../helpers/api-client";
import { Course } from "./hooks/useFetchVerityAssessmentInfo";
import { useTimingBlockValidation } from "./hooks/useValidateVerityAssessment";

const SCORING_STRATEGIES: ScoringStrategy[] = ["first", "latest", "highest"];

const baseKey = `${baseTransKey}.assessmentDetails`;

type DetailBlockLabelProps = {
	title: ReactNode;
	description?: ReactNode;
	id?: string;
};

function DetailBlockLabel({ title, description, id }: DetailBlockLabelProps) {
	return (
		<div className={styles.detailLabel}>
			<Typography variant="h3" className={styles.title} id={id}>
				{title}
			</Typography>
			{description && <Typography className={styles.description}>{description}</Typography>}
		</div>
	);
}

type DetailBlockRowProps = Pick<DetailBlockLabelProps, "description" | "title"> & {
	createControl: (id: string) => ReactNode;
};

function DetailBlockRow({ createControl, title, description }: DetailBlockRowProps) {
	const id = useId();
	return (
		<>
			<DetailBlockLabel title={title} description={description} id={id} />
			{createControl(id)}
		</>
	);
}

type DetailBlockProps = {
	children: ReactNode;
	title: ReactNode;
	openOnMount?: boolean;
	forceOpen?: boolean;
};

function DetailBlock({ children, title, openOnMount = true, forceOpen }: DetailBlockProps) {
	const [expanded, setExpanded] = useState<boolean>(openOnMount);

	useEffect(() => {
		if (forceOpen) {
			setExpanded(true);
		}
	}, [forceOpen]);

	return (
		<div className={styles.detailBlock}>
			<Button onClick={() => setExpanded(!expanded)} className={styles.detailButton} aria-expanded={expanded}>
				<CoreExpandIcon expanded={expanded} variant="narrow" />
				<Typography variant="h2" className={styles.detailTitle}>
					{title}
				</Typography>
			</Button>
			{expanded && <div className={styles.detailGrid}>{children}</div>}
		</div>
	);
}

type CommonDetailBlockProps = {
	forceOpen?: boolean;
	isEditing: boolean;
};

type CourseGradeBlockProps = {
	lms?: Lms;
	courses: Course[];
	selectedCourseId?: number;
	onCourseChange?: (courseId: number) => void;
	isSynced: boolean;
	onSyncChange: (synced: boolean) => void;
	courseSearchProps: CoreSelectSearchProps;
	modules: Module[];
	selectedModuleId?: string;
	onModuleChange?: (moduleId: string, courseId?: number) => void;
	moduleName?: string;
	onModuleNameChange?: (name: string) => void;
	fetchingModules?: boolean;
	assignmentMaxScoreInput?: string;
	onAssignmentMaxScoreChange: (score: string) => void;
	type: VerityAssessmentType;
} & CommonDetailBlockProps;

export function CourseGradeBlock({
	lms,
	courses,
	selectedCourseId,
	onCourseChange,
	isSynced,
	onSyncChange,
	modules,
	selectedModuleId,
	onModuleChange,
	moduleName,
	onModuleNameChange,
	fetchingModules,
	isEditing,
	assignmentMaxScoreInput,
	onAssignmentMaxScoreChange,
	type,
	courseSearchProps,
	...props
}: CourseGradeBlockProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { courseId } = portalContext;

	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [creatingModule, setCreatingModule] = useState(false);

	const syncText = useMemo(() => {
		if (lms) {
			return i18n.t(`${baseKey}.courseGrade.syncText`) as string;
		}

		return undefined;
	}, [lms]);

	const isCourseValid = useMemo(() => isSelectedCourseValid(selectedCourseId, courses), [selectedCourseId, courses]);
	const isModuleValid = useMemo(() => isSelectedModuleValid(selectedModuleId, modules), [selectedModuleId, modules]);

	const lmsSupportsModules = useMemo(() => usesModules(lms), [lms]);
	const isMoodle = lms === "moodle";

	const filteredCourses = useMemo(() => {
		if (!courseSearchProps.value) return courses;
		return courses.filter(
			(course) => course.name && course.name.toLowerCase().includes(courseSearchProps.value.toLowerCase()),
		);
	}, [courses, courseSearchProps.value]);

	const handleCreateModule = useCallback(async () => {
		if (!moduleName || !lmsSupportsModules || !selectedCourseId) return;
		try {
			setCreatingModule(true);
			const item = isMoodle
				? await apiClient.course.createCourseRootSection(selectedCourseId, moduleName)
				: await apiClient.course.createCourseRootModule(selectedCourseId, moduleName);
			onModuleChange?.(item.id, selectedCourseId);
			coreToast.success(i18n.t(`${baseKey}.courseGrade.moduleCreateSuccess`, { name: moduleName }) as string);
			setCreateModalOpen(false);
		} catch (e) {
			console.error(e);
			coreToast.error(i18n.t(`${baseKey}.courseGrade.moduleCreateError`, { name: moduleName }) as string);
		} finally {
			setCreatingModule(false);
		}
	}, [moduleName, lmsSupportsModules, selectedCourseId, onModuleChange, isMoodle]);

	// Select current course or first course if no selected course
	useEffect(() => {
		if (!selectedCourseId && courses.length > 0) {
			onCourseChange?.(courseId ?? courses[0].id);
		}
	}, [selectedCourseId, courses, onCourseChange, courseId]);

	// Select first module if LMS supports modules and there are modules available
	useEffect(() => {
		if (lmsSupportsModules && modules.length > 0 && !selectedModuleId) {
			onModuleChange?.(modules[0].id);
		}
	}, [lmsSupportsModules, modules, selectedModuleId, onModuleChange]);

	const pointsErrorMessage = getAssignmentPointsErrorMessage(assignmentMaxScoreInput);

	return (
		<DetailBlock {...props} title={i18n.t(`${baseKey}.courseGrade.title`) as string}>
			{courses.length > 0 && (
				<DetailBlockRow
					title={i18n.t(`${baseKey}.courseGrade.selectCourse`) as string}
					description={i18n.t(`${baseKey}.courseGrade.selectCourseDesc`) as string}
					createControl={(id) => {
						return (
							<CoreSelectTextField<number>
								id={id}
								disabled={isEditing}
								value={selectedCourseId ?? ""}
								onChange={(event) => onCourseChange?.(Number(event.target.value))}
								ariaLabelledBy={id}
								popperMaxHeight="200px"
								renderValue={(value) => {
									const course = courses.find((c) => c.id === Number(value));
									return course ? course.name : "";
								}}
								className={styles.courseSelect}
								error={!isCourseValid ? (i18n.t(`${baseKey}.courseGrade.courseError`) as string) : undefined}
								searchProps={courseSearchProps}
							>
								{filteredCourses.map((course) => (
									<CoreMenuItem key={course.id} value={course.id}>
										<ListItemText>{course.name}</ListItemText>
									</CoreMenuItem>
								))}
							</CoreSelectTextField>
						);
					}}
				/>
			)}
			{syncText && (
				<DetailBlockRow
					title={syncText}
					createControl={(id) => {
						return (
							<CoreSwitch
								ariaLabelledBy={id}
								checked={isSynced}
								onChange={(event) => onSyncChange(event.target.checked)}
								labelTextType="left"
							/>
						);
					}}
				/>
			)}
			{lmsSupportsModules && (modules.length > 0 || fetchingModules) && (
				<DetailBlockRow
					title={i18n.t(`${baseKey}.courseGrade.${isMoodle ? "section" : "module"}`) as string}
					description={i18n.t(`${baseKey}.courseGrade.${isMoodle ? "sectionDesc" : "moduleDesc"}`) as string}
					createControl={(id) => (
						<CoreSkeletonWrapper loading={fetchingModules} width="100%">
							<CoreSelectTextField<string>
								id={id}
								value={selectedModuleId ?? ""}
								disabled={isEditing}
								onChange={(event) => onModuleChange?.(event.target.value)}
								ariaLabelledBy={id}
								popperMaxHeight="200px"
								renderValue={(value) => {
									const module = getModule(modules, value);
									return module ? module.name : "";
								}}
								className={styles.courseSelect}
								error={
									!isModuleValid
										? (i18n.t(`${baseKey}.courseGrade.${isMoodle ? "sectionError" : "moduleError"}`) as string)
										: undefined
								}
							>
								{modules.map((module) => (
									<CoreMenuItem key={module.id} value={module.id}>
										<ListItemText>{module.name}</ListItemText>
									</CoreMenuItem>
								))}
							</CoreSelectTextField>
						</CoreSkeletonWrapper>
					)}
				/>
			)}
			{type === "assignment" && (
				<DetailBlockRow
					title={i18n.t(`${baseKey}.courseGrade.assignmentMaxScore`) as string}
					createControl={(id) => (
						<div className={styles.durationInputs}>
							<CoreTextField
								id={id}
								type="number"
								value={assignmentMaxScoreInput}
								onChange={(e) => onAssignmentMaxScoreChange(e.target.value)}
								ariaLabelledBy={id}
								placeholder={i18n.t(`${baseKey}.courseGrade.assignmentMaxScorePlaceholder`) as string}
								error={Boolean(pointsErrorMessage)}
								sx={{ maxWidth: "150px" }}
							/>
							{pointsErrorMessage && <CoreErrorIndicator error={pointsErrorMessage} size={18} />}
						</div>
					)}
				/>
			)}
			{lmsSupportsModules && modules.length === 0 && !fetchingModules && !isEditing && (
				<CoreBanner
					color="secondary"
					label={i18n.t(`${baseKey}.courseGrade.${isMoodle ? "noSection" : "noModule"}`) as string}
					className={styles.noModuleBanner}
				>
					<CoreButton onClick={() => setCreateModalOpen(true)}>
						{i18n.t(`${baseKey}.courseGrade.${isMoodle ? "createSection" : "createModule"}`)}
					</CoreButton>
				</CoreBanner>
			)}
			{createModalOpen && (
				<CoreModal
					onClose={() => setCreateModalOpen(false)}
					title={i18n.t(`${baseKey}.courseGrade.create`) as string}
					open
					buttons={[
						{
							text: i18n.t(`${baseKey}.courseGrade.cancel`) as string,
							onClick: () => setCreateModalOpen(false),
							color: "secondary",
						},
						{
							text: i18n.t(`${baseKey}.courseGrade.${isMoodle ? "createSection" : "createModule"}`) as string,
							onClick: handleCreateModule,
							disabled: !moduleName || creatingModule,
							loading: creatingModule,
						},
					]}
				>
					<Stack spacing={1}>
						<CoreModalDescription>
							{i18n.t(`${baseKey}.courseGrade.${isMoodle ? "createSectionDesc" : "createDesc"}`) as string}
						</CoreModalDescription>
						<CoreTextField
							label={
								i18n.t(`${baseKey}.courseGrade.${isMoodle ? "sectionPlaceholder" : "modulePlaceholder"}`) as string
							}
							value={moduleName}
							onChange={(e) => onModuleNameChange?.(e.target.value)}
							size="small"
							maxLength={128}
							alwaysShowLength
						/>
					</Stack>
				</CoreModal>
			)}
		</DetailBlock>
	);
}

type GeneralInfoBlockProps = {
	assessmentTitle: string;
	assessmentDescription: string;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
} & CommonDetailBlockProps;

export function GeneralInfoBlock({
	assessmentTitle,
	assessmentDescription,
	onTitleChange,
	onDescriptionChange,
	...props
}: GeneralInfoBlockProps) {
	const hasTitleError = useMemo(() => !isAssessmentTitleValid(assessmentTitle), [assessmentTitle]);

	return (
		<DetailBlock {...props} title={i18n.t(`${baseKey}.generalInfo.title`) as string}>
			<DetailBlockRow
				title={i18n.t(`${baseKey}.generalInfo.assessmentTitle`) as string}
				createControl={(id) => (
					<CoreTextField
						id={id}
						value={assessmentTitle}
						onChange={(e) => onTitleChange(e.target.value)}
						ariaLabelledBy={id}
						placeholder={i18n.t(`${baseKey}.generalInfo.assessmentTitlePlaceholder`) as string}
						error={hasTitleError ? (i18n.t(`${baseKey}.generalInfo.assessmentTitleError`) as string) : undefined}
						maxLength={128}
						alwaysShowLength
					/>
				)}
			/>
			<DetailBlockRow
				title={i18n.t(`${baseKey}.generalInfo.assessmentDesc`) as string}
				description={i18n.t(`${baseKey}.generalInfo.assessmentDescSubtitle`) as string}
				createControl={(id) => (
					<CoreTextField
						id={id}
						value={assessmentDescription}
						onChange={(e) => onDescriptionChange(e.target.value)}
						ariaLabelledBy={id}
						placeholder={i18n.t(`${baseKey}.generalInfo.assessmentDescPlaceholder`) as string}
						multiline
						minRows={2}
						maxRows={6}
					/>
				)}
			/>
		</DetailBlock>
	);
}

export type AssessmentDuration = {
	hour: string;
	minute: string;
};

type TimingBlockProps = {
	assessmentDuration: number | null;
	durationHourInput: string;
	durationMinInput: string;
	onAssessmentDurationChange: (hour: string, min: string) => void;
	resetAssessmentDuration: () => void;
	startDate: Date;
	endDate: Date;
	onStartDateChange: (date: Date | null) => void;
	onEndDateChange: (date: Date | null) => void;
	allowLateSubmissions: boolean;
	onAllowLateSubmissionsChange: (allow: boolean) => void;
	lateSubmissionDate: Date | null;
	onLateSubmissionDateChange: (date: Date | null) => void;
} & CommonDetailBlockProps;

export function TimingBlock({
	durationHourInput,
	durationMinInput,
	onAssessmentDurationChange,
	resetAssessmentDuration,
	assessmentDuration: rawAssessmentDuration,
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	allowLateSubmissions,
	onAllowLateSubmissionsChange,
	lateSubmissionDate,
	onLateSubmissionDateChange,
	...props
}: TimingBlockProps) {
	const lateId = useId();

	const { endErrorMessage, hourErrorMessage, minuteErrorMessage, startErrorMessage, lateSubmissionDateErrorMessage } =
		useTimingBlockValidation({
			startDate,
			endDate,
			allowLateSubmissions,
			lateSubmissionDate,
			assessmentDuration: { hour: durationHourInput, minute: durationMinInput },
		});

	return (
		<DetailBlock {...props} title={i18n.t(`${baseKey}.timing.title`) as string}>
			<DetailBlockRow
				title={i18n.t(`${baseKey}.timing.assessmentDuration`) as string}
				description={i18n.t(`${baseKey}.timing.assessmentDurationDesc`) as string}
				createControl={(id) => {
					return (
						<div className={styles.assessmentDurationControl}>
							<CoreSwitch
								ariaLabelledBy={id}
								checked={rawAssessmentDuration !== null}
								onChange={(e) => {
									if (e.target.checked) {
										onAssessmentDurationChange("3", "0");
									} else {
										resetAssessmentDuration();
									}
								}}
								labelTextType="left"
							/>
							{rawAssessmentDuration !== null && (
								<>
									<div className={styles.durationInputs}>
										<CoreTextField
											type="number"
											value={durationHourInput}
											onChange={(e) => {
												onAssessmentDurationChange(e.target.value, durationMinInput);
											}}
											error={Boolean(hourErrorMessage)}
											useFloatingLabel
											label={i18n.t(`${baseKey}.timing.hours`) as string}
										/>
										{hourErrorMessage && <CoreErrorIndicator error={hourErrorMessage} size={18} />}
									</div>
									<div className={styles.durationInputs}>
										<CoreTextField
											type="number"
											value={durationMinInput}
											onChange={(e) => {
												onAssessmentDurationChange(durationHourInput, e.target.value);
											}}
											error={Boolean(minuteErrorMessage)}
											useFloatingLabel
											label={i18n.t(`${baseKey}.timing.minutes` as string) as string}
										/>
										{minuteErrorMessage && <CoreErrorIndicator error={minuteErrorMessage} size={18} />}
									</div>
								</>
							)}
						</div>
					);
				}}
			/>
			<DetailBlockRow
				title={i18n.t(`${baseKey}.timing.dueDate`) as string}
				description={i18n.t(`${baseKey}.timing.dueDateDesc`) as string}
				createControl={() => {
					return (
						<div className={styles.dateTimePickerWrapper}>
							<CoreDateTimePicker
								value={startDate}
								onChange={onStartDateChange}
								useButton
								dialogAriaLabel={i18n.t(`${baseKey}.timing.startDateAriaLabel`) as string}
								error={startErrorMessage}
								actions={["cancel", "clear", "accept"]}
							/>
							<CoreErrorIndicator error={startErrorMessage} size={18} />
							<ArrowRight />
							<CoreDateTimePicker
								value={endDate}
								onChange={onEndDateChange}
								useButton
								minDateTime={startDate ?? undefined}
								dialogAriaLabel={i18n.t(`${baseKey}.timing.endDateAriaLabel`) as string}
								error={endErrorMessage}
								disablePast
								actions={["cancel", "clear", "accept"]}
							/>
							<CoreErrorIndicator error={endErrorMessage} size={18} />
						</div>
					);
				}}
			/>
			<div className={styles.lateSubmissionWrapper}>
				<DetailBlockLabel
					title={i18n.t(`${baseKey}.timing.late`) as string}
					description={i18n.t(`${baseKey}.timing.lateDesc`) as string}
					id={lateId}
				/>
				<div className={styles.lateSubmissionControl}>
					<CoreSwitch
						checked={allowLateSubmissions}
						onChange={(e) => onAllowLateSubmissionsChange(e.target.checked)}
						ariaLabelledBy={lateId}
						labelTextType="left"
					/>
					{allowLateSubmissions && (
						<FormControlLabel
							control={
								<div className={styles.dateTimePickerWrapper}>
									<CoreDateTimePicker
										value={lateSubmissionDate}
										onChange={onLateSubmissionDateChange}
										useButton
										dialogAriaLabel={i18n.t(`${baseKey}.timing.acceptSubmission`) as string}
										disablePast
										error={lateSubmissionDateErrorMessage}
									/>
									<CoreErrorIndicator error={lateSubmissionDateErrorMessage} size={18} />
								</div>
							}
							label={i18n.t(`${baseKey}.timing.acceptSubmission`) as string}
							labelPlacement="top"
							className={styles.lateSubmissionDate}
						/>
					)}
				</div>
			</div>
		</DetailBlock>
	);
}

type SubmissionBlockProps = {
	allowMultipleAttempts: boolean;
	onAllowMultipleAttemptsChange: (allow: boolean) => void;
	numberOfAttempts: number;
	onNumberOfAttemptsChange: (attempts: number) => void;
	allowAllFiles: boolean;
	onAllowAllFilesChange: (allow: boolean) => void;
	allowedFileTypes: FileTypeOption[];
	onAllowedFileTypesChange: (types: FileTypeOption[]) => void;
	assessmentType: VerityAssessmentType;
	showScoreOnSubmission: boolean;
	onShowScoreOnSubmissionChange: (show: boolean) => void;
	scoringStrategy: ScoringStrategy;
	onScoringStrategyChange: (strategy: ScoringStrategy) => void;
} & CommonDetailBlockProps;

export function SubmissionBlock({
	allowMultipleAttempts,
	onAllowMultipleAttemptsChange,
	numberOfAttempts,
	onNumberOfAttemptsChange,
	allowAllFiles,
	onAllowAllFilesChange,
	allowedFileTypes,
	onAllowedFileTypesChange,
	assessmentType,
	showScoreOnSubmission,
	onShowScoreOnSubmissionChange,
	scoringStrategy,
	onScoringStrategyChange,
	...props
}: SubmissionBlockProps) {
	return (
		<DetailBlock {...props} title={i18n.t(`${baseKey}.submission.title`) as string}>
			<DetailBlockRow
				title={i18n.t(`${baseKey}.submission.multiple`) as string}
				description={i18n.t(`${baseKey}.submission.multipleDesc`) as string}
				createControl={(id) => (
					<div className={styles.multipleAttemptsControl}>
						<CoreSwitch
							ariaLabelledBy={id}
							checked={allowMultipleAttempts}
							onChange={(event) => onAllowMultipleAttemptsChange(event.target.checked)}
							labelTextType="left"
						/>
						{allowMultipleAttempts && (
							<CoreSelectTextField<number>
								value={numberOfAttempts}
								onChange={(e) => onNumberOfAttemptsChange(Number(e.target.value))}
								ariaLabelledBy={id}
								popperMaxHeight="200px"
								renderValue={(value) =>
									value === UNLIMITED_ATTEMPTS ? (i18n.t(`${baseKey}.submission.unlimited`) as string) : value
								}
								className={styles.attemptsSelect}
							>
								{Array.from({ length: 9 }, (_, i) => i + 2).map((attempt) => (
									<CoreMenuItem key={attempt} value={attempt}>
										<ListItemText>{attempt}</ListItemText>
									</CoreMenuItem>
								))}
								<CoreMenuItem value={UNLIMITED_ATTEMPTS}>
									<ListItemText>{i18n.t(`${baseKey}.submission.unlimited`)}</ListItemText>
								</CoreMenuItem>
							</CoreSelectTextField>
						)}
					</div>
				)}
			/>
			{allowMultipleAttempts && (
				<DetailBlockRow
					title={i18n.t(`${baseKey}.submission.gradeBook`) as string}
					description={i18n.t(`${baseKey}.submission.gradeBookDesc`) as string}
					createControl={(id) => (
						<CoreSelectTextField<ScoringStrategy>
							id={id}
							value={scoringStrategy}
							onChange={(e) => onScoringStrategyChange(e.target.value as ScoringStrategy)}
							ariaLabelledBy={id}
							popperMaxHeight="200px"
							sx={{ maxWidth: "300px" }}
							renderValue={(value) => i18n.t(`${baseKey}.submission.${value}`) as string}
						>
							{SCORING_STRATEGIES.map((strategy) => (
								<CoreMenuItem key={strategy} value={strategy}>
									<ListItemText>{i18n.t(`${baseKey}.submission.${strategy}`) as string}</ListItemText>
								</CoreMenuItem>
							))}
						</CoreSelectTextField>
					)}
				/>
			)}
			{assessmentType === "assignment" && (
				<DetailBlockRow
					title={i18n.t(`${baseKey}.submission.fileFormat`) as string}
					description={i18n.t(`${baseKey}.submission.fileFormatDesc`) as string}
					createControl={(id) => (
						<div className={styles.fileTypeControl}>
							<CoreSwitch
								ariaLabelledBy={id}
								checked={!allowAllFiles}
								onChange={(event) => onAllowAllFilesChange(!event.target.checked)}
								labelTextType="left"
							/>
							{!allowAllFiles && (
								<CoreFileTypeSelector
									selectedOptions={allowedFileTypes}
									onChange={(selected) => onAllowedFileTypesChange(selected)}
									placeholder={i18n.t(`${baseKey}.submission.fileFormatPlaceholder`) as string}
									slotProps={{
										textField: {
											className: styles.fileTypeSelector,
										},
									}}
								/>
							)}
						</div>
					)}
				/>
			)}
			{assessmentType === "quiz" && (
				<DetailBlockRow
					title={i18n.t(`${baseKey}.submission.showScore`) as string}
					description={i18n.t(`${baseKey}.submission.showScoreDesc`) as string}
					createControl={(id) => (
						<CoreSwitch
							ariaLabelledBy={id}
							checked={showScoreOnSubmission}
							onChange={(event) => onShowScoreOnSubmissionChange(event.target.checked)}
							labelTextType="left"
						/>
					)}
				/>
			)}
		</DetailBlock>
	);
}
